import type {ReactElement} from 'react';
import React from 'react';
import {DefaultAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-option';
import type {AnimationPanelType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {PreviewType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {useGetKeyFromItemIntroAnimation} from '@Hooks/poster-editor/useGetKeyFromItemIntroAnimation';
import {useGetKeyFromItemOutroAnimation} from '@Hooks/poster-editor/useGetKeyFromItemOutroAnimation';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

export interface DefaultAnimationOptionProps {
  onAnimationTypeClick: (type: AnimationPanelType) => void;
}

export function ItemAnimationOption({...props}: DefaultAnimationOptionProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const introAnimation = useGetKeyFromItemIntroAnimation('type');
  const outroAnimation = useGetKeyFromItemOutroAnimation('type');

  if (!activeItem) {
    throw new Error('Unable to show animation options since the activeItem is null');
  }

  if (!introAnimation || !outroAnimation) {
    return null;
  }

  const getIntroPreviewType = (): PreviewType => {
    if (activeItem.isText()) {
      return PreviewType.TEXT;
    }
    if (activeItem.isSlideshow() && activeItem.slides.getFirstSlide().isTextSlide()) {
      return PreviewType.TEXT;
    }
    if (activeItem.isSticker() || activeItem.isRectangle() || activeItem.isLine() || activeItem.isVector()) {
      return PreviewType.ELEMENT;
    }
    return PreviewType.MEDIA;
  };

  const getOutroPreviewType = (): PreviewType => {
    if (activeItem.isText()) {
      return PreviewType.TEXT;
    }
    if (activeItem.isSlideshow() && activeItem.slides.getLastSlide().isTextSlide()) {
      return PreviewType.TEXT;
    }
    if (activeItem.isSticker() || activeItem.isRectangle() || activeItem.isLine() || activeItem.isVector()) {
      return PreviewType.ELEMENT;
    }
    return PreviewType.MEDIA;
  };

  return (
    <DefaultAnimationOption
      key={'item-level-animation-option'}
      introAnimation={introAnimation}
      outroAnimation={outroAnimation}
      onAnimationTypeClick={props.onAnimationTypeClick}
      introPreviewType={getIntroPreviewType()}
      outroPreviewType={getOutroPreviewType()}
    />
  );
}
